import { alpha, createTheme, Theme } from "@mui/material/styles";
import { AppearanceTheme, COLOR_SLOTS, ThemeColors } from "./uiConfig.types";
import { resolveThemeColors } from "./uiConfig.slots";
import { isColorValid } from "../muiUtils";

export type BrandingMode = "light" | "dark";

// Mode resolution for the branded Login/Register pages (NG-8457):
//   ?mode=light|dark  →  config.defaultMode (system → OS prefers-color-scheme)  →  light fallback.
export const resolveBrandingMode = (urlMode: string | null | undefined, defaultMode: AppearanceTheme | undefined): BrandingMode => {
    if (urlMode === "light" || urlMode === "dark") {
        return urlMode;
    }
    if (defaultMode === AppearanceTheme.LIGHT) {
        return "light";
    }
    if (defaultMode === AppearanceTheme.DARK) {
        return "dark";
    }
    // "system" (or unset): follow the OS preference, otherwise light.
    if (typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    return "light";
};

// Build a scoped MUI theme from a branding config's colors for the given mode. Each of the slots is
// written to `palette.branding.<slot>` (page styles read those directly; seedDefaultBranding supplies the
// same slots from theme tokens when branding is off, so the pages never carry literal fallbacks), and the
// slots that also drive standard MUI channels (form background, body/secondary text, error, success, input
// border, disabled button) are mapped through so built-in components restyle too. Invalid color values
// fall back to the base theme's resolved default for that slot (same defensive behavior as isColorValid).
export const buildBrandingTheme = (base: Theme, colors?: ThemeColors, mode: BrandingMode = "light"): Theme => {
    const defaults = resolveThemeColors(base);
    const branding = COLOR_SLOTS.reduce((acc, slot) => {
        const value = colors?.[slot];
        acc[slot] = value && isColorValid(value) ? value : defaults[slot];
        return acc;
    }, {} as ThemeColors);

    // Auto-derive light/dark/contrastText the way MUI does (augmentColor applies the theme's tonalOffset).
    // createTheme(base, overrides) deep-merges the second arg but does NOT augment it, so we augment here
    // explicitly — otherwise error/success would keep the base theme's .light/.dark/.contrastText (wrong
    // hue) and the branded buttons would get no derived hover shade (NG-8457 AC-5). The buttons stay styled
    // via the branding slots (not color="primary") on purpose: the app primary (Trust Blue) differs from the
    // CTA accent (merigold), so hijacking palette.primary would recolor generic primary elements off-brand.
    const primaryAction = base.palette.augmentColor({ color: { main: branding.primaryActionBg } });
    const secondaryAction = base.palette.augmentColor({ color: { main: branding.secondaryActionBg } });
    const error = base.palette.augmentColor({ color: { main: branding.errorColor } });
    const success = base.palette.augmentColor({ color: { main: branding.successColor } });
    // Expose the `links` slot as a themeable `palette.hyperlink` color so link-style elements consume the
    // standard token (color="hyperlink" / palette.hyperlink) instead of reaching into palette.branding.
    const hyperlink = base.palette.augmentColor({ color: { main: branding.links } });

    // NG-8598 dark-mode legibility: the app theme pins `typography.allVariants.color` to its palette ink
    // (Charcoal in light mode, Cloud in dark), and createTypography bakes that color into every variant
    // object (body1, h1, …). Anything styled from a typography variant — MenuItem (`...theme.typography.body1`),
    // ListItem, every <Typography> without an explicit color — therefore ignores palette.text.primary and
    // renders the baked ink: ~1.3:1 against an opposite-mode formBackground, which is why the open Select
    // options were unreadable. No branding slot could reach it.
    // Re-point each variant that carries a color at the bodyText slot, so the slot drives all body/heading
    // text. In light mode bodyText defaults to text.primary (#313B47) — the same value — so nothing changes.
    const typography = Object.fromEntries(
        Object.entries(base.typography)
            .filter(([, value]) => !!value && typeof value === "object" && "color" in value)
            .map(([variant]) => [variant, { color: branding.bodyText }]),
    );

    return createTheme(base, {
        palette: {
            mode,
            branding: {
                ...branding,
                // Derived hover shades consumed by the Login/Register button styles.
                primaryActionBgHover: primaryAction.dark,
                secondaryActionBgHover: secondaryAction.dark,
            },
            background: { default: branding.formBackground, paper: branding.formBackground },
            text: { primary: branding.bodyText, secondary: branding.secondaryText },
            error,
            success,
            hyperlink,
            action: { disabledBackground: branding.disabledButtonBg },
            // Route inputBorder through the standard `divider` token too, so neutral borders (OTP boxes,
            // card outlines) can consume `palette.divider` instead of reaching into palette.branding.
            divider: branding.inputBorder,
        },
        typography,
        components: {
            MuiOutlinedInput: {
                styleOverrides: {
                    // Give inputs a subtle surface so they don't look transparent against a branded form
                    // background (NG-8457 QA). A translucent white overlay lifts the field off any
                    // formBackground and adapts to mode (barely-there in dark, lighter in light). Applied
                    // only within the branded theme, so the default app theme is untouched.
                    root: {
                        backgroundColor: alpha(base.palette.common.white, mode === "dark" ? 0.09 : 0.5),
                        // The app theme bakes its own text.primary into the input at theme build, which
                        // doesn't follow the branding slots and is unreadable on an opposite-mode
                        // formBackground — the typed value and the Select's displayed value both live here.
                        color: branding.bodyText,
                    },
                    notchedOutline: { borderColor: branding.inputBorder },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    // MUI derives the option hover/selected overlays from palette.action (light-mode alphas of
                    // black — createTheme(base, …) deep-merges `mode` without re-running createPalette, so they
                    // stay black even in dark) and tints the selected row with palette.primary, which the
                    // branded theme deliberately leaves alone. Both are invisible here, so drive them off the
                    // branding slots: a bodyText wash for hover, the primary-action accent for the selection.
                    root: {
                        "&:hover": { backgroundColor: alpha(branding.bodyText, 0.08) },
                        "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: alpha(branding.primaryActionBg, mode === "dark" ? 0.32 : 0.18),
                        },
                        // The "Month"/"Day"/"Year" placeholder rows are disabled items; MUI's 0.38 opacity
                        // leaves them near-invisible on a dark surface.
                        "&.Mui-disabled": { opacity: 0.6 },
                    },
                },
            },
        },
    });
};

// Off-state seed (NG-8457 follow-up): expose the theme-derived slot defaults under `palette.branding` so
// the Login/Register styles can read `palette.branding.<slot>` uniformly — on or off — instead of
// duplicating each default as a literal fallback in the page style files. It also seeds the one standard
// channel that branding-agnostic components rely on — `divider` (the input/neutral border) — so those
// components consume the standard token consistently whether branding is on or off. Other standard
// channels (background/text/error/…) are intentionally left untouched to preserve today's unbranded look;
// hover shades are set to the base color (not augmented) to keep the exact no-hover unbranded look.
export const seedDefaultBranding = (base: Theme): Theme => {
    const defaults = resolveThemeColors(base);
    return createTheme(base, {
        palette: {
            branding: {
                ...defaults,
                primaryActionBgHover: defaults.primaryActionBg,
                secondaryActionBgHover: defaults.secondaryActionBg,
            },
            divider: defaults.inputBorder,
            // Seed palette.hyperlink from the theme-derived default so link-style elements read the standard
            // token consistently whether branding is on or off.
            hyperlink: base.palette.augmentColor({ color: { main: defaults.links } }),
        },
    });
};
